import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { FiltersPanel } from '@/components/shared/FiltersPanel';
import { selectedRiskAssessmentFilters } from '../../state';
import { useFiltersForRisk } from '../hooks/useFiltersForRisk';
import { ActionButton } from '../shared/styles';
import { SelectedFiltersType } from '@/types';

type ReviewOptionsContainerProps = {
  loading: boolean;
  handleSearch: Dispatch<SetStateAction<string>>;
  handleSetHideContainer: (isHidden: boolean) => void;
};

export const ReviewOptionsContainer = ({
  loading,
  handleSearch,
  handleSetHideContainer,
}: ReviewOptionsContainerProps) => {
  const [isExpandedClicked, setIsExpandedClicked] = useState(false);
  const [selectedFilters, setSelectedFilters] = useAtom(selectedRiskAssessmentFilters);
  const { filters, loading: filtersLoading } = useFiltersForRisk({
    source: 'review',
    selectedFilters,
  });
  const initialFiltersReference = useRef<SelectedFiltersType | undefined>(undefined);

  useEffect(() => {
    if (initialFiltersReference.current) return;
    const initialFilters = selectedFilters.reduce((accumulator, { id, value }) => {
      if (!accumulator[id]) {
        accumulator[id] = [];
      }
      accumulator[id].push({ key: value, label: value });
      return accumulator;
    }, {} as SelectedFiltersType);

    initialFiltersReference.current = initialFilters;
  }, [selectedFilters]);

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, string[]>) => {
      const appliedFilters = Object.entries(newFilters).map(([key, values]) =>
        values.map((value) => ({ id: key, value }))
      );
      setSelectedFilters(appliedFilters.flat());
    },
    [setSelectedFilters]
  );

  useEffect(() => {
    handleSetHideContainer(isExpandedClicked);
  }, [handleSetHideContainer, isExpandedClicked]);

  const handleExpandView = useCallback(() => {
    setIsExpandedClicked(!isExpandedClicked);
  }, [isExpandedClicked]);

  return (
    <Box sx={{ mt: 3 }}>
      <FiltersPanel
        filters={filters}
        initialSelectedFilters={initialFiltersReference.current}
        disabled={filtersLoading}
        loading={loading || filtersLoading}
        onApply={handleFiltersChange}
        handleSearch={handleSearch}
        rightComponent={
          <Stack direction="row" alignItems="center" gap={'1.2rem'}>
            <ActionButton compact data-testId="expand-view-button" onClick={handleExpandView}>
              <AbyssIcon
                iconName={isExpandedClicked ? 'collapse' : 'expand'}
                style={{ width: '1.8rem', height: '1.8rem' }}
              />
              <span>{isExpandedClicked ? 'Exit' : 'Expand'} View</span>
            </ActionButton>
          </Stack>
        }
      />
    </Box>
  );
};
